import { ChangeDetectionStrategy, Component, computed, effect, input, signal } from '@angular/core'
import { FormArray, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms'
import { AmountWithCurrencyForm, FreetextSection } from '@features/certificate/models/certificate-shared.model'
import { LiabilityCoverProvisionsForm } from '@features/certificate/models/liability-cover-provisions/liability-cover-provisions.model'
import { CertificateBaseComponent } from '@features/certificate/components/certificate-base/certificate-base.component'
import { AlbertCheckboxComponent } from '@shared/components/albert-checkbox/albert-checkbox.component'
import { AlbertInputWrapper } from '@features/certificate/components/certificate-input-wrapper/albert-input-wrapper'
import { AlbertInputComponent } from '@shared/components/albert-input/albert-input.component'
import { AlbertLimitComponent } from '@shared/components/albert-limit/albert-limit.component'
import { AlbertSelectComponent } from '@shared/components/albert-select/albert-select.component'
import { ButtonDirective } from 'primeng/button'
import { Paginator, PaginatorState } from 'primeng/paginator'
import { CertificateFreetextSectionComponent } from '@features/certificate/components/certificate-freetext-section/certificate-freetext-section.component'
import { EU785_COVER_PACKAGE, EU785_COVER_PACKAGE_OPTIONS, SDR_CURRENCY } from '@features/certificate/models/certificate-select-options.model'
import {
  additionalInsuredSection,
  breachOfWarrantySection,
  freetextSection,
  noLiabilityForPremiumsSection,
  noticeOfCancellationNoWarSection,
  setupFreetextSections
} from '@features/certificate/utils/freetext-utils'
import { IfCategoryDirective } from '@features/certificate/directives/if-category.directive'
import { LIABILITY_COVER_PROVISIONS_VISIBILITY } from '@features/certificate/components/liability-cover-provisions/liability-cover-provisions.visibility'
import { CertificateCategory } from '@app/shared/models/certificate-category.model'
import { formRevision, syncFormCategoryState } from '@core/utils/form-utils'
import { TooltipModule } from 'primeng/tooltip'
import { map, merge, of, switchMap } from 'rxjs'
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop'

type FreetextSectionKey =
  | 'additionalFreetextLiability'
  | 'bhvPrimaryFreetext'
  | 'additionalInsured'
  | 'primaryClause'
  | 'severabilityOfInterest'
  | 'noLiabilityForPremiums'
  | 'breachOfWarranty'
  | 'noticeOfCancellationNoWar'
  | 'additionalFreetext'

@Component({
  selector: 'albert-liability-cover-provisions',
  imports: [
    ReactiveFormsModule,
    AlbertCheckboxComponent,
    AlbertInputComponent,
    AlbertInputWrapper,
    AlbertLimitComponent,
    AlbertSelectComponent,
    CertificateFreetextSectionComponent,
    IfCategoryDirective,
    ButtonDirective,
    Paginator,
    TooltipModule
  ],
  templateUrl: './liability-cover-provisions.component.html',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LiabilityCoverProvisionsComponent extends CertificateBaseComponent {
  public readonly form = input.required<FormGroup<LiabilityCoverProvisionsForm>>()
  public readonly aircraftTypeLimitRows = 5
  public readonly aircraftTypeLimitFirst = signal<number>(0)
  // Form values are no signals: bumped on every change, so the template follows the checkboxes and the limits
  // loaded from the insured.
  private readonly revision = formRevision(this.form)
  public readonly showAircraftTypeLimits = computed(() => {
    this.revision()
    return this.form().controls.liabilityLimitsPerAircraftType.value === true
  })
  /** The total limit is replaced by the limits per aircraft type, on the fleet list or various ones. */
  public readonly avn52eTotalLimitLocked = computed(() => {
    this.revision()
    const controls = this.form().controls
    return controls.liabilityLimitsPerAircraftType.value === true
      || controls.liabilityAvn52eLimitsFleetList.value === true
      || controls.liabilityAvn52eVariousLimits.value === true
  })
  public readonly pagedAircraftTypeLimits = computed(() => {
    this.revision()
    const first = this.aircraftTypeLimitFirst()
    return this.liabilityLimitAmountAircraft.controls
      .map((limitGroup, index) => ({ limitGroup, typeControlName: index.toString(), index }))
      .slice(first, first + this.aircraftTypeLimitRows)
  })
  public readonly eu785CoverPackageOptions = EU785_COVER_PACKAGE_OPTIONS
  public readonly freetextSections: FreetextSection<FreetextSectionKey>[] = [
    freetextSection('additionalFreetextLiability', 'Additional Freetext Liability', 'additional-freetext-liability'),
    freetextSection('bhvPrimaryFreetext', 'BHV Primary Freetext', 'bhv-primary-freetext'),
    additionalInsuredSection('liability', 'Additional Insured Freetext'),
    freetextSection('primaryClause', 'Primary Clause Freetext', 'primary-clause', 'Primary Clause'),
    freetextSection('severabilityOfInterest', 'Severability of Interest Freetext', 'severability-of-interest', 'Severability of Interest'),
    noLiabilityForPremiumsSection('No Liability for Premiums'),
    breachOfWarrantySection(),
    noticeOfCancellationNoWarSection('notice-of-cancellation-no-war'),
    freetextSection('additionalFreetext', 'Freetext Liability Provisions', 'freetext-liability-provisions')
  ]
  public readonly freetextSectionsByKey: Record<FreetextSectionKey, FreetextSection> =
    setupFreetextSections(this.form, this.freetextSections)
  public readonly sdrEnabled = signal<boolean>(false)
  public readonly visibility = LIABILITY_COVER_PROVISIONS_VISIBILITY
  public readonly CertificateCategory = CertificateCategory

  constructor() {
    super()
    // Fewer limits (e.g. all removed by unticking) must not leave the paginator behind its last page.
    effect(() => {
      this.revision()
      this.clampAircraftTypePage()
    })

    toObservable(this.form).pipe(
      switchMap(form => merge(
        of(false),
        form.controls.eu785CoverPackage.valueChanges.pipe(map(value => value !== null && value !== EU785_COVER_PACKAGE.NONE))
      ).pipe(map(selected => ({ form, selected })))),
      takeUntilDestroyed()
    ).subscribe(({ form, selected }) => {
      this.updateSdr(form)
      if (selected) {
        form.controls.sdrAmount.markAllAsTouched()
      }
    })

    toObservable(computed(() => ({ form: this.form(), canEdit: this.canEdit() }))).pipe(
      switchMap(state => merge(
        of(false),
        state.form.controls.bhvPrimary.valueChanges.pipe(map(value => value === true))
      ).pipe(map(ticked => ({ ...state, ticked })))),
      takeUntilDestroyed()
    ).subscribe(({ form, canEdit, ticked }) => {
      if (canEdit && form.controls.bhvPrimary.value === true) {
        form.controls.bhvPrimaryAmount.enable()
      } else {
        form.controls.bhvPrimaryAmount.disable()
      }
      if (ticked) {
        form.controls.bhvPrimaryAmount.markAllAsTouched()
      }
    })

    toObservable(this.form).pipe(
      switchMap(form => merge(
        of(false),
        merge(
          form.controls.liabilityLimitsPerAircraftType.valueChanges,
          form.controls.liabilityAvn52eLimitsFleetList.valueChanges,
          form.controls.liabilityAvn52eVariousLimits.valueChanges
        ).pipe(map(() => true))
      ).pipe(map(userToggled => ({ form, userToggled })))),
      takeUntilDestroyed()
    ).subscribe(({ form, userToggled }) => this.resetAvn52eTotalLimitAmount(form, userToggled))

    effect(() => {
      const category = this.categoryService.activeCertificateCategory()
      syncFormCategoryState(this.form(), category, this.visibility)
    })
  }

  public get liabilityLimitAmountAircraft(): FormArray<FormGroup<AmountWithCurrencyForm>> {
    return this.form().controls.liabilityLimitAmountAircraft
  }

  public get liabilityAircraftType(): FormArray<FormControl<string>> {
    return this.form().controls.liabilityAircraftType
  }

  public onClearAircraftTypeLimit(index: number): void {
    // Marked dirty first: the autosave only picks up a change it sees on an already dirty form.
    this.liabilityLimitAmountAircraft.at(index).markAsDirty()
    this.liabilityLimitAmountAircraft.at(index).patchValue({
      amount: null,
      currency: null
    })
  }

  public onAircraftTypePageChange(event: PaginatorState): void {
    this.aircraftTypeLimitFirst.set(event.first ?? 0)
  }

  private updateSdr(form: FormGroup<LiabilityCoverProvisionsForm>): void {
    const val = form.controls.eu785CoverPackage.value
    const isActive = val !== null && val !== EU785_COVER_PACKAGE.NONE
    this.sdrEnabled.set(isActive)
    if (isActive) {
      form.controls.sdrAmount.enable()
      // Only the amount is editable, the currency stays SDR.
      form.controls.sdrAmount.controls.currency.setValue(SDR_CURRENCY)
      form.controls.sdrAmount.controls.currency.disable()
    } else {
      form.controls.sdrAmount.reset({ amount: null, currency: SDR_CURRENCY })
      form.controls.sdrAmount.disable()
    }
  }

  private resetAvn52eTotalLimitAmount(form: FormGroup<LiabilityCoverProvisionsForm>, userToggled: boolean): void {
    const avn52eTotalLimitAmountCtrl = form.controls.avn52eTotalLimitAmount
    if (form.controls.liabilityLimitsPerAircraftType.value !== true
      && form.controls.liabilityAvn52eLimitsFleetList.value !== true
      && form.controls.liabilityAvn52eVariousLimits.value !== true) return
    if (userToggled) {
      avn52eTotalLimitAmountCtrl.markAsDirty()
    }
    avn52eTotalLimitAmountCtrl.controls.amount.setValue(null, { emitEvent: userToggled })
    avn52eTotalLimitAmountCtrl.controls.currency.setValue(null, { emitEvent: userToggled })
  }

  private clampAircraftTypePage(): void {
    const length = this.liabilityLimitAmountAircraft.length
    if (this.aircraftTypeLimitFirst() >= length) {
      const lastPageFirst = Math.max(0, (Math.ceil(length / this.aircraftTypeLimitRows) - 1) * this.aircraftTypeLimitRows)
      this.aircraftTypeLimitFirst.set(lastPageFirst)
    }
  }
}
